import { supabase } from "@/integrations/supabase/client";

export interface AdminChapter {
  id: number;
  order: number;
  certificationId: string;
  chapterId: string;
  title: string;
  description: string;
  status: "active" | "draft";
  questionsTotal: number;
  questionsCompleted: number;
  lastScore: number;
  kLevels: { K1: number; K2: number; K3: number };
  isWeak: boolean;
  parentChapterId?: string;
  level: number;
}

type Listener = () => void;
let _items: AdminChapter[] = [];
let _loaded = false;
const listeners = new Set<Listener>();
const notify = () => listeners.forEach((fn) => fn());

export function rowToChapter(r: any): AdminChapter {
  return {
    id: r.sort_order ?? 0,
    order: r.sort_order ?? 0,
    certificationId: r.certification_id,
    chapterId: r.chapter_id,
    title: r.title,
    description: r.description ?? "",
    status: r.status as "active" | "draft",
    questionsTotal: r.questions_total ?? 0,
    questionsCompleted: r.questions_completed ?? 0,
    lastScore: r.last_score ?? 0,
    kLevels: (r.k_levels as any) ?? { K1: 0, K2: 0, K3: 0 },
    isWeak: r.is_weak ?? false,
    parentChapterId: r.parent_chapter_id ?? undefined,
    level: r.level ?? 0,
  };
}

async function loadFromDb() {
  const { data } = await supabase.from("chapters").select("*").order("sort_order");
  _items = (data ?? []).map(rowToChapter);
  _loaded = true;
  notify();
}

loadFromDb();

export function getChapterNumber(chapterId: string, allChapters: AdminChapter[]): string {
  const chapter = allChapters.find((c) => c.chapterId === chapterId);
  if (!chapter) return "";
  const parts: number[] = [];
  let current: AdminChapter | undefined = chapter;
  while (current) {
    const parentId = current.parentChapterId || undefined;
    const siblings = allChapters
      .filter((c) => c.certificationId === chapter.certificationId && (c.parentChapterId || undefined) === parentId)
      .sort((a, b) => a.order - b.order);
    const position = siblings.findIndex((c) => c.chapterId === current!.chapterId) + 1;
    parts.unshift(position);
    current = parentId ? allChapters.find((c) => c.chapterId === parentId) : undefined;
  }
  return parts.join(".");
}

export const chapterStore = {
  get: () => _items,
  subscribe: (fn: Listener) => { listeners.add(fn); return () => listeners.delete(fn); },
  isLoaded: () => _loaded,
  reload: loadFromDb,

  async add(item: AdminChapter) {
    const { error } = await supabase.from("chapters").insert({
      chapter_id: item.chapterId,
      certification_id: item.certificationId,
      title: item.title,
      description: item.description,
      status: item.status,
      sort_order: item.order,
      parent_chapter_id: item.parentChapterId ?? null,
      level: item.level,
      questions_total: item.questionsTotal,
      questions_completed: item.questionsCompleted,
      last_score: item.lastScore,
      k_levels: item.kLevels as unknown as null,
      is_weak: item.isWeak,
    });
    if (!error) {
      _items = [..._items, item];
      notify();
    }
    return { error };
  },

  async update(chapterId: string, patch: Partial<AdminChapter>) {
    const dbPatch: Record<string, unknown> = {};
    if (patch.title !== undefined) dbPatch.title = patch.title;
    if (patch.description !== undefined) dbPatch.description = patch.description;
    if (patch.status !== undefined) dbPatch.status = patch.status;
    if (patch.order !== undefined) dbPatch.sort_order = patch.order;
    if (patch.parentChapterId !== undefined) dbPatch.parent_chapter_id = patch.parentChapterId ?? null;
    if (patch.level !== undefined) dbPatch.level = patch.level;
    if (patch.questionsTotal !== undefined) dbPatch.questions_total = patch.questionsTotal;
    if (patch.questionsCompleted !== undefined) dbPatch.questions_completed = patch.questionsCompleted;
    if (patch.lastScore !== undefined) dbPatch.last_score = patch.lastScore;
    if (patch.kLevels !== undefined) dbPatch.k_levels = patch.kLevels;
    if (patch.isWeak !== undefined) dbPatch.is_weak = patch.isWeak;

    const { error } = await supabase.from("chapters").update(dbPatch).eq("chapter_id", chapterId);
    if (!error) {
      _items = _items.map((c) => (c.chapterId === chapterId ? { ...c, ...patch } : c));
      notify();
    }
  },

  async remove(chapterId: string) {
    const { error } = await supabase.from("chapters").delete().eq("chapter_id", chapterId);
    if (!error) {
      _items = _items.filter((c) => c.chapterId !== chapterId);
      notify();
    }
  },

  async reorder(certificationId: string, chapterId: string, direction: "up" | "down") {
    const chapter = _items.find((c) => c.chapterId === chapterId);
    if (!chapter) return;
    const parentId = chapter.parentChapterId || undefined;
    const siblings = _items
      .filter((c) => c.certificationId === certificationId && (c.parentChapterId || undefined) === parentId)
      .sort((a, b) => a.order - b.order);
    const idx = siblings.findIndex((c) => c.chapterId === chapterId);
    if (idx < 0) return;
    const swapIdx = direction === "up" ? idx - 1 : idx + 1;
    if (swapIdx < 0 || swapIdx >= siblings.length) return;
    const currentOrder = siblings[idx].order;
    const swapOrder = siblings[swapIdx].order;
    _items = _items.map((c) => {
      if (c.chapterId === chapterId) return { ...c, order: swapOrder };
      if (c.chapterId === siblings[swapIdx].chapterId) return { ...c, order: currentOrder };
      return c;
    });
    notify();
    await supabase.from("chapters").update({ sort_order: swapOrder }).eq("chapter_id", chapterId);
    await supabase.from("chapters").update({ sort_order: currentOrder }).eq("chapter_id", siblings[swapIdx].chapterId);
  },

  async reorderByIds(certificationId: string, parentChapterId: string | undefined, orderedIds: string[]) {
    const siblings = _items.filter(
      (c) => c.certificationId === certificationId && (c.parentChapterId || undefined) === parentChapterId
    );
    if (siblings.length !== orderedIds.length) return;
    const sorted = [...siblings].sort((a, b) => a.order - b.order);
    const orders = sorted.map((s) => s.order);
    const idToOrder = new Map(orderedIds.map((id, i) => [id, orders[i]]));
    _items = _items.map((c) => {
      const newOrder = idToOrder.get(c.chapterId);
      return newOrder !== undefined ? { ...c, order: newOrder } : c;
    });
    notify();
    for (const [chId, order] of idToOrder) {
      await supabase.from("chapters").update({ sort_order: order }).eq("chapter_id", chId);
    }
  },

  getChildren(parentChapterId: string): AdminChapter[] {
    return _items.filter((c) => c.parentChapterId === parentChapterId).sort((a, b) => a.order - b.order);
  },

  getParent(chapterId: string): AdminChapter | undefined {
    const ch = _items.find((c) => c.chapterId === chapterId);
    if (!ch?.parentChapterId) return undefined;
    return _items.find((c) => c.chapterId === ch.parentChapterId);
  },

  getBreadcrumb(chapterId: string): AdminChapter[] {
    const result: AdminChapter[] = [];
    let current = _items.find((c) => c.chapterId === chapterId);
    while (current) {
      result.unshift(current);
      current = current.parentChapterId ? _items.find((c) => c.chapterId === current!.parentChapterId) : undefined;
    }
    return result;
  },
};

export function getChapterAndDescendantIds(chapterId: string, chapters: AdminChapter[]): Set<string> {
  const ids = new Set<string>([chapterId]);
  const collect = (parentId: string) => {
    chapters.filter(c => c.parentChapterId === parentId).forEach(c => {
      ids.add(c.chapterId);
      collect(c.chapterId);
    });
  };
  collect(chapterId);
  return ids;
}

/**
 * Returns only top-level chapters (no parent), optionally scoped to a
 * certification. Sub-chapters / sections are excluded — matches the
 * "Chapters" count shown on the landing certification dialog.
 */
export function getTopLevelChapters(
  chapters: AdminChapter[],
  certificationId?: string,
): AdminChapter[] {
  return chapters.filter(
    (c) =>
      !c.parentChapterId &&
      (certificationId === undefined || c.certificationId === certificationId),
  );
}

/**
 * Count of top-level chapters for a certification (excludes sub-chapters).
 */
export function countTopLevelChapters(
  chapters: AdminChapter[],
  certificationId?: string,
): number {
  return getTopLevelChapters(chapters, certificationId).length;
}
